/**
 * ====================================================================
 * 360° Watch Viewer — Frame-Based Engine for Shopify
 * ====================================================================
 */
(function () {
  'use strict';

  const CONFIG = {
    EXTRACT_FPS: 30,           // Frames per second to extract
    SENSITIVITY: 1.0,          // Pixels of drag per frame (1.0 = 1px maps to 1 frame)
    LERP_SPEED: 0.18,          // Smoothing
    MOMENTUM_FRICTION: 0.96,   // Drift decay
    AUTO_SPEED: 0.2,           // Speed of auto-rotation
    AUTO_RESTART_DELAY: 2000,  // ms before resume auto-rotate
  };

  class Watch360Viewer {
    constructor(container) {
      this.container = container;
      this.videoUrl = container.getAttribute('data-watch-360-video');
      
      // DOM Elements
      this.overlay = container.querySelector('.watch-360-overlay');
      this.canvas = container.querySelector('.viewer-canvas');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.trigger = container.querySelector('.watch-360-trigger-btn');
      this.closeBtn = container.querySelector('.watch-360-close-btn');
      this.loader = container.querySelector('.loading-overlay');
      this.hint = container.querySelector('.w360-hint');
      this.autoPlayBtn = container.querySelector('.watch-360-autoplay-btn');
      this.autoPlayStatus = container.querySelector('.autoplay-status');

      // Memory state
      this.frames = [];
      this.totalFrames = 0;

      // Position state
      this.currentFrame = -1;
      this.targetFrame = 0;
      this.displayFrame = 0;

      // Interaction state
      this.isActive = false;
      this.framesLoaded = false;
      this.framesLoading = false;
      this.dragging = false;
      this.dragStartX = 0;
      this.lastVel = 0;
      this.lastMoveTime = 0;
      this.hintDismissed = false;

      // Animation loop
      this.renderLoopId = null;
      this.autoPlayEnabled = false; // Auto Play globally toggled off by default
      this.autoRotating = false;
      this.momentum = 0;
      this.autoRestartTimer = null;

      // Global frame cache
      if (!window.__w360_cache) window.__w360_cache = {};

      this._bindEvents();
    }

    _bindEvents() {
      if (this.trigger) {
        this.trigger.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.open(); });
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.close(); });
      }

      // Close background click functionality removed per user request (viewer only closes on [X] button)
      // if (this.overlay) {
      //   this.overlay.addEventListener('click', (e) => {
      //     if (e.target === this.overlay) this.close();
      //   });
      // }

      if (this.autoPlayBtn) {
        this.autoPlayBtn.addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation();
          this.autoPlayEnabled = !this.autoPlayEnabled;
          this.autoRotating = this.autoPlayEnabled;
          
          if (this.autoPlayStatus) {
            this.autoPlayStatus.textContent = this.autoPlayEnabled ? 'On' : 'Off';
          }
          if (this.autoPlayEnabled) {
            this.autoPlayBtn.classList.add('is-active');
            if (!this.hintDismissed && this.hint) {
              this.hint.classList.add('is-hidden');
              this.hintDismissed = true;
            }
          } else {
            this.autoPlayBtn.classList.remove('is-active');
          }
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener('mousedown', (e) => this._onDown(e));
        this.overlay.addEventListener('touchstart', (e) => this._onDown(e), { passive: false });
      }

      // Capture 'move' and 'up' globally on window to ensure dragging continues
      // even if the mouse leaves the browser window or viewer container.
      window.addEventListener('mousemove', (e) => this._onMove(e));
      window.addEventListener('mouseup', () => this._onUp());
      window.addEventListener('touchmove', (e) => this._onMove(e), { passive: false });
      window.addEventListener('touchend', () => this._onUp());
      window.addEventListener('touchcancel', () => this._onUp());

      window.addEventListener('resize', () => {
        if (this.isActive) { this._resize(); this._drawCurrent(); }
      });
    }

    async open() {
      this.isActive = true;
      this.overlay.removeAttribute('hidden');
      this.overlay.setAttribute('aria-hidden', 'false');
      
      this._resize();

      if (!this.framesLoaded && !this.framesLoading) {
        this.framesLoading = true;
        if (this.loader) this.loader.classList.remove('is-hidden');

        try {
          await this._extractFrames();
          this.framesLoaded = true;
          this.framesLoading = false;
          if (this.loader) this.loader.classList.add('is-hidden');
          this._resize();
          this._drawFrame(0);

          if (!this.hintDismissed && this.hint && !this.autoPlayEnabled) {
            this.hint.classList.remove('is-hidden');
          }

          this.autoRotating = this.autoPlayEnabled;
          this._startRenderLoop();
        } catch (err) {
          console.error('[Watch360] Frame extraction failed:', err);
          this.framesLoading = false;
          const lt = this.loader?.querySelector('.loading-text');
          if (lt) lt.textContent = 'Error loading 360 view.';
        }
      } else if (this.framesLoaded) {
        if (this.loader) this.loader.classList.add('is-hidden');
        if (!this.hintDismissed && this.hint && !this.autoPlayEnabled) {
            this.hint.classList.remove('is-hidden');
        }
        this.autoRotating = this.autoPlayEnabled;
        this._startRenderLoop();
      }
    }

    close() {
      this.isActive = false;
      this.autoRotating = false;
      this.momentum = 0;
      this._stopRenderLoop();
      clearTimeout(this.autoRestartTimer);
      this.overlay.setAttribute('hidden', '');
      this.overlay.setAttribute('aria-hidden', 'true');
    }

    _extractFrames() {
      if (!this.videoUrl) return Promise.reject(new Error('No video URL provided'));
      if (window.__w360_cache[this.videoUrl]) {
        this.frames = window.__w360_cache[this.videoUrl];
        this.totalFrames = this.frames.length;
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const v = document.createElement('video');
        v.muted = true;
        v.playsInline = true;
        v.preload = 'auto';
        v.crossOrigin = 'anonymous';
        v.src = this.videoUrl;

        const extracted = [];
        let duration, interval, seekTime = 0;

        v.addEventListener('loadedmetadata', () => {
          duration = v.duration;
          interval = 1 / CONFIG.EXTRACT_FPS;
          const offC = document.createElement('canvas');
          offC.width = v.videoWidth;
          offC.height = v.videoHeight;
          const offCtx = offC.getContext('2d');

          const next = () => {
            if (seekTime >= duration) {
              window.__w360_cache[this.videoUrl] = extracted;
              this.frames = extracted;
              this.totalFrames = extracted.length;
              v.src = '';
              resolve();
              return;
            }
            v.currentTime = seekTime;
          };

          v.addEventListener('seeked', () => {
            offCtx.clearRect(0, 0, offC.width, offC.height);
            offCtx.drawImage(v, 0, 0);
            createImageBitmap(offC).then(bmp => {
              extracted.push(bmp);
              const pct = Math.round((seekTime / duration) * 100);
              const lt = this.loader?.querySelector('.loading-text');
              if (lt) lt.textContent = `Loading 360° View... ${pct}%`;
              seekTime += interval;
              next();
            }).catch(() => {
              seekTime += interval;
              next();
            });
          });
          next();
        });
        v.addEventListener('error', () => reject(new Error('Failed mapping video')));
      });
    }

    _startRenderLoop() {
      if (this.renderLoopId) return;
      let last = performance.now();

      const tick = (now) => {
        if (!this.isActive) { this._stopRenderLoop(); return; }
        const dt = Math.min((now - last) / 16.667, 3);
        last = now;

        if (this.autoRotating && !this.dragging) {
          this.targetFrame += CONFIG.AUTO_SPEED * dt;
        }

        if (Math.abs(this.momentum) > 0.01) {
          this.targetFrame += this.momentum * dt;
          this.momentum *= Math.pow(CONFIG.MOMENTUM_FRICTION, dt);
        } else {
          this.momentum = 0;
        }

        if (this.totalFrames > 0) {
          while (this.targetFrame < 0) this.targetFrame += this.totalFrames;
          while (this.targetFrame >= this.totalFrames) this.targetFrame -= this.totalFrames;
        }

        let diff = this.targetFrame - this.displayFrame;
        if (this.totalFrames > 0) {
          if (diff > this.totalFrames / 2) diff -= this.totalFrames;
          if (diff < -this.totalFrames / 2) diff += this.totalFrames;
        }
        this.displayFrame += diff * CONFIG.LERP_SPEED * dt;

        if (this.totalFrames > 0) {
          while (this.displayFrame < 0) this.displayFrame += this.totalFrames;
          while (this.displayFrame >= this.totalFrames) this.displayFrame -= this.totalFrames;
        }

        const idx = Math.floor(this.displayFrame) % this.totalFrames;
        if (idx !== this.currentFrame) {
          this.currentFrame = idx;
          this._drawFrame(idx);
        }

        this.renderLoopId = requestAnimationFrame(tick);
      };

      this.renderLoopId = requestAnimationFrame(tick);
    }

    _stopRenderLoop() {
      if (this.renderLoopId) { cancelAnimationFrame(this.renderLoopId); this.renderLoopId = null; }
    }

    _drawFrame(idx) {
      if (!this.ctx || !this.frames[idx]) return;
      const f = this.frames[idx];
      const cw = this.canvas.width, ch = this.canvas.height;
      const fw = f.width || cw, fh = f.height || ch;
      const scale = Math.min(cw / fw, ch / fh);
      const w = fw * scale, h = fh * scale;
      const x = (cw - w) / 2, y = (ch - h) / 2;
      this.ctx.clearRect(0, 0, cw, ch);
      this.ctx.drawImage(f, x, y, w, h);
    }

    _drawCurrent() {
      if (this.framesLoaded && this.currentFrame >= 0) this._drawFrame(this.currentFrame);
    }

    _resize() {
      if (!this.canvas) return;
      // Since canvas is in a box with aspect-ratio: 1/1, we size by bounding client rect.
      // Easiest is to traverse up to .watch-360-viewer which flexes correctly.
      const parent = this.canvas.parentElement;
      if (!parent) return;
      
      const r = parent.getBoundingClientRect();
      if (r.width && r.height) { 
        this.canvas.width = r.width; 
        this.canvas.height = r.height; 
      }
    }

    _getX(e) {
      return (e.touches && e.touches.length) ? e.touches[0].clientX : e.clientX;
    }

    _onDown(e) {
      if (!this.isActive || !this.framesLoaded) return;
      if (e.target.closest('button')) return; // ignore clicks on buttons
      e.preventDefault();
      
      this.dragging = true;
      this.dragStartX = this._getX(e);
      this.lastMoveTime = performance.now();
      this.lastVel = 0;
      this.momentum = 0;
      this.autoRotating = false;
      clearTimeout(this.autoRestartTimer);

      if (this.overlay) {
          this.overlay.style.cursor = 'grabbing';
      }

      if (!this.hintDismissed && this.hint) {
        this.hint.classList.add('is-hidden');
        this.hintDismissed = true;
      }
    }

    _onMove(e) {
      if (!this.dragging || !this.isActive) return;
      e.preventDefault();
      const x = this._getX(e);
      const dx = x - this.dragStartX;
      this.dragStartX = x;
      const frameDelta = dx / CONFIG.SENSITIVITY;
      this.targetFrame -= frameDelta; // Negative rotation (drag left -> frame increases)
      const now = performance.now();
      const dt = now - this.lastMoveTime;
      if (dt > 0) {
        this.lastVel = -frameDelta / dt;
      }
      this.lastMoveTime = now;
    }

    _onUp() {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.overlay) {
          this.overlay.style.cursor = '';
      }

      if (Math.abs(this.lastVel) > 0.002) {
        this.momentum = -this.lastVel * 16;
      }

      clearTimeout(this.autoRestartTimer);
      if (this.autoPlayEnabled) {
        this.autoRestartTimer = setTimeout(() => {
          if (this.isActive && !this.dragging) {
            this.autoRotating = true;
          }
        }, CONFIG.AUTO_RESTART_DELAY);
      }
    }
  }

  function init() {
    document.querySelectorAll('.watch-360-integration-container').forEach(el => {
      if (!el._w360) el._w360 = new Watch360Viewer(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
